import { useState, useEffect, useCallback, useRef } from 'react';
import { api, fmt } from '../api';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import NavIcon from '../components/NavIcon';
import DateField from '../components/DateField';

function SaleModal({ products, onClose, onSave }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [items, setItems] = useState([{ productId: '', quantity: 1, unitPrice: '' }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems(i => [...i, { productId: '', quantity: 1, unitPrice: '' }]);
  const removeItem = (idx) => setItems(i => i.filter((_, j) => j !== idx));
  const setItem = (idx, k, v) => setItems(i => {
    const next = [...i]; next[idx] = { ...next[idx], [k]: v };
    if (k === 'productId' && v) { const p = products.find(p => p.id === Number(v)); if (p) next[idx].unitPrice = p.salePrice; }
    return next;
  });
  const total = items.reduce((s, i) => s + (Number(i.quantity) * Number(i.unitPrice) || 0), 0);

  const save = async () => {
    const valid = items.filter(i => i.productId && i.quantity > 0 && i.unitPrice > 0);
    if (!valid.length) return toast.error('Add at least one valid item');
    setSaving(true);
    try {
      await api.createSale({ date: new Date(date).toISOString(), note, items: valid.map(i => ({ productId: Number(i.productId), quantity: Number(i.quantity), unitPrice: Number(i.unitPrice) })) });
      toast.success('Sale recorded!'); onSave();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header"><h3>Record Sale</h3><button className="btn btn-secondary btn-sm" onClick={onClose}>✕</button></div>
        <div className="form-row">
          <div className="field"><label>Date</label><DateField value={date} onChange={e => setDate(e.target.value)} /></div>
          <div className="field"><label>Note (optional)</label><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Walk-in customer" /></div>
        </div>
        <div className="section-title mt-4">Items</div>
        <div className="item-row-header">
          {['Product','Qty','Unit Price',''].map(h => <div key={h} style={{fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.6px'}}>{h}</div>)}
        </div>
        <div className="sale-items">
          {items.map((item, idx) => (
            <div className="sale-item-row" key={idx}>
              <select style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,color:'var(--text)',padding:'8px 10px',fontSize:13,outline:'none'}} value={item.productId} onChange={e => setItem(idx,'productId',e.target.value)}>
                <option value="">Select product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock})</option>)}
              </select>
              <input type="number" min="0.01" step="0.01" style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,color:'var(--text)',padding:'8px',fontSize:13,outline:'none',textAlign:'right'}} value={item.quantity} onChange={e => setItem(idx,'quantity',e.target.value)} />
              <input type="number" min="0" step="0.01" style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:7,color:'var(--text)',padding:'8px',fontSize:13,outline:'none',textAlign:'right'}} value={item.unitPrice} onChange={e => setItem(idx,'unitPrice',e.target.value)} />
              <button className="btn btn-danger btn-sm" onClick={() => removeItem(idx)} style={{padding:'8px 10px'}}>✕</button>
            </div>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm mt-4" onClick={addItem}>+ Add Item</button>
        <div className="sale-total mt-4"><span>Total Amount</span><span style={{fontSize:20,color:'var(--accent2)'}}>{fmt.currency(total)}</span></div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : '✓ Save Sale'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Sales() {
  const [result, setResult]     = useState({ items: [], totalCount: 0, page: 1, pageSize: 20, totalPages: 1 });
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState(null);

  // filter state
  const [search, setSearch]     = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // debounce search
  const searchTimer = useRef();
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const loadSales = useCallback(() => {
    api.getSales({ page, pageSize, search: debouncedSearch || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined })
      .then(setResult).catch(() => toast.error('Failed to load sales'));
  }, [page, pageSize, debouncedSearch, dateFrom, dateTo]);

  useEffect(() => { loadSales(); }, [loadSales]);
  useEffect(() => { api.getProductsLookup().then(setProducts); }, []);

  const clearFilters = () => { setSearch(''); setDateFrom(''); setDateTo(''); setPage(1); };
  const hasFilters = search || dateFrom || dateTo;

  return (
    <div>
      <div className="flex-between mb-6">
        <div className="page-header" style={{margin:0}}>
          <h2>Sales</h2>
          <p>{result.totalCount} total transactions</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Sale</button>
      </div>

      <div className="card">
        <div className="filter-bar">
          <input placeholder="Search by product or note…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <DateField value={dateFrom} title="From date"
            onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
          <DateField value={dateTo} title="To date"
            onChange={e => { setDateTo(e.target.value); setPage(1); }} />
          {hasFilters && <button className="filter-clear" onClick={clearFilters}>✕ Clear</button>}
          <div className="filter-spacer"/>
          <span className="filter-results">{result.totalCount} transactions</span>
        </div>

        {result.items.length === 0 ? (
          <div className="empty-state"><div className="icon"><NavIcon name="sales" size={32} /></div><div>No sales found.</div></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Date</th><th>Items</th><th>Total</th><th>Note</th></tr></thead>
                <tbody>
                  {result.items.map(s => {
                    const total = s.items?.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0) || 0;
                    const isOpen = expanded === s.id;
                    return (
                      <>
                        <tr key={s.id} style={{cursor:'pointer'}} onClick={() => setExpanded(isOpen ? null : s.id)}>
                          <td className="mono" data-label="Date">{fmt.datetime(s.date)}</td>
                          <td data-label="Items"><span className="badge badge-blue">{s.items?.length || 0} items</span></td>
                          <td className="mono text-green" data-label="Total" style={{fontWeight:700}}>{fmt.currency(total)}</td>
                          <td className="text-muted" data-label="Note">{s.note || '—'}</td>
                        </tr>
                        {isOpen && (
                          <tr key={`${s.id}-detail`}>
                            <td colSpan={4} style={{background:'var(--surface2)',padding:'10px 20px'}}>
                              <table style={{width:'100%'}}>
                                <thead><tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Subtotal</th></tr></thead>
                                <tbody>
                                  {s.items?.map(i => (
                                    <tr key={i.id}>
                                      <td>{i.product?.name}</td>
                                      <td className="mono">{i.quantity}</td>
                                      <td className="mono">{fmt.currency(i.unitPrice)}</td>
                                      <td className="mono text-green">{fmt.currency(i.quantity * i.unitPrice)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={result.page} totalPages={result.totalPages} pageSize={result.pageSize}
              totalCount={result.totalCount} onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }} />
          </>
        )}
      </div>

      {showModal && <SaleModal products={products} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); loadSales(); }} />}
    </div>
  );
}
